import { useState, useEffect, useContext, useCallback } from "react";
import { PlusIcon, PencilIcon, TrashIcon, ArrowRightIcon, GlobeIcon, CoinIcon, BookOpenIcon, MagnetIcon, TagIcon, CurrencyEurIcon, FunnelIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

import { toastOptions, constants } from "../../../../assets/constants";
import { AuthContext } from "../../../../contexts";
import { CustomTag, CustomModal, CustomSearchInput } from "../../../../ui";
import RateOverrides from "./RateOverrides";

// "other" is a fallback type set by the AltelisBooks backfill: it is displayed but can't be created manually
const selectableEngineTypes = constants.engineTypes.filter((engineType) => engineType.value !== "other");

// Config objects the server sends only for engines that have a stored config
const engineConfigKeys = ["dedgeConfig", "reservitConfig", "mewsConfig", "misterBookingConfig", "amenitizConfig"];

const Engines = ({ contact, onEngineChange }) => {
    const { token } = useContext(AuthContext);
    const [engines, setEngines] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEngine, setEditingEngine] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [dedgeGroups, setDedgeGroups] = useState([]);
    // Engine whose metasearch export rate exclusions are being edited
    const [rateOverridesEngine, setRateOverridesEngine] = useState(null);

    const [config, setConfig] = useState({
        altelisId: contact?.reference,
        stayTax: null,
        dedgeConfig: {
            groupId: null,
        },
        reservitConfig: {},
        mewsConfig: {},
        misterBookingConfig: {},
        amenitizConfig: {},
    });

    const fetchEngines = useCallback(async () => {
        if (!contact?.reference) {
            setEngines([]);
            setIsLoaded(true);
            return;
        }

        try {
            setIsRefreshing(true);
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}engines/configs/altelis/${contact.reference}`, { headers: { Authorization: `Bearer ${token}` } });
            setEngines(response.data);
            setIsLoaded(true);
        } catch (error) {
            toast.error(`Erreur lors du chargement des configurations: ${error.response?.data?.message || error.message}`, toastOptions);
            setIsLoaded(true);
        } finally {
            setIsRefreshing(false);
        }
    }, [contact?.reference, token]);

    const fetchDedgeGroups = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}engines/groups`, { headers: { Authorization: `Bearer ${token}` } });
            setDedgeGroups(response.data);
        } catch (error) {
            toast.error(`Erreur lors du chargement des groupes D-EDGE: ${error.response?.data?.message || error.message}`, toastOptions);
        }
    }, [token]);

    useEffect(() => {
        fetchEngines();
        fetchDedgeGroups();
    }, [fetchEngines, fetchDedgeGroups]);

    const validateEngineConfig = (config) => {
        const required = ["engineType", "hotelName", "engineCode", "engineUrl", "stayTax"];
        return required.every((field) => config[field]?.toString().trim());
    };

    const handleCreateEngine = async () => {
        if (!validateEngineConfig(config)) {
            toast.error("Veuillez remplir tous les champs obligatoires (Type moteur, Nom de l'hôtel, Code moteur, URL moteur, Taxe de séjour)", toastOptions);
            return;
        }

        setIsCreating(true);
        try {
            if (isEditing && editingEngine) {
                await axios.put(`${process.env.REACT_APP_UPA_API_HOST}engines/${editingEngine.id}`, config, { headers: { Authorization: `Bearer ${token}` } });
                toast.success("Configuration moteur modifiée avec succès", toastOptions);
            } else {
                await axios.post(`${process.env.REACT_APP_UPA_API_HOST}engines`, config, { headers: { Authorization: `Bearer ${token}` } });
                toast.success("Configuration moteur créée avec succès", toastOptions);
            }

            setIsModalOpen(false);
            resetForm();
            fetchEngines();
            onEngineChange && onEngineChange();
        } catch (error) {
            toast.error(`Erreur lors de ${isEditing ? "la modification" : "la création"}: ${error.response?.data?.message || error.message}`, toastOptions);
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteEngine = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}engines/${deleteTarget.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Configuration moteur supprimée avec succès", toastOptions);
            fetchEngines();
            onEngineChange && onEngineChange();
        } catch (error) {
            toast.error(`Erreur lors de la suppression: ${error.response?.data?.message || error.message}`, toastOptions);
        } finally {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
        }
    };

    const resetForm = () => {
        setConfig({
            altelisId: contact?.reference,

            stayTax: null,
            dedgeConfig: {
                groupId: null,
            },
            reservitConfig: {},
            mewsConfig: {},
            misterBookingConfig: {},
            amenitizConfig: {},
        });
        setEditingEngine(null);
        setIsEditing(false);
    };

    const handleOpenModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleEditEngine = (engine) => {
        setConfig({
            ...engine,
            altelisId: contact?.reference,

            engineType: engine.engineType,
            dedgeConfig: {
                groupId: null,
                ...engine.dedgeConfig,
            },
            reservitConfig: engine.reservitConfig || {},
            mewsConfig: engine.mewsConfig || {},
            misterBookingConfig: engine.misterBookingConfig || {},
            amenitizConfig: engine.amenitizConfig || {},
        });
        setEditingEngine(engine);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleRemoveEngine = (engine) => {
        setDeleteTarget(engine);
        setDeleteModalOpen(true);
    };

    const getEngineTypeLabel = (engineType) => {
        const type = constants.engineTypes.find((type) => type.value === engineType);
        return type?.label || engineType;
    };

    const getStatusVariant = (engineType) => {
        if (engineType === "dedge") return "purple";
        if (engineType === "reservit") return "warning";
        if (engineType === "mews") return "blue";
        if (engineType === "mister_booking") return "success";
        if (engineType === "amenitiz") return "info";
        if (engineType === "brit_hotels") return "success";
        if (engineType === "namastay") return "purple";
        if (engineType === "synxis") return "warning";
        if (engineType === "cloudbeds") return "blue";
        if (engineType === "thais") return "yellow";
        if (engineType === "siteminder") return "purple";
        if (engineType === "best_western") return "info";
        if (engineType === "the_originals") return "yellow";
        if (engineType === "other") return "default";
        return "default";
    };

    const filteredEngines = engines.filter((engine) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            engine.hotelName?.toLowerCase().includes(searchLower) ||
            engine.engineCode?.toLowerCase().includes(searchLower) ||
            engine.engineUrl?.toLowerCase().includes(searchLower) ||
            getEngineTypeLabel(engine.engineType)?.toLowerCase().includes(searchLower)
        );
    });

    // AltelisBooks deletes the config of the previous type when the type changes
    const isConfigDeletedOnSave =
        isEditing && editingEngine !== null && config.engineType !== editingEngine.engineType && engineConfigKeys.some((key) => editingEngine[key]);

    const getSelectedEngineType = () => {
        if (!config.engineType) return null;
        const entityTypes = constants.engineTypes.map((engineType) => ({ ...engineType, id: engineType.value }));
        return entityTypes.find((type) => type.value === config.engineType) || null;
    };

    const getSelectedDedgeGroup = () => {
        if (!config?.dedgeConfig?.groupId || dedgeGroups.length === 0) return null;
        const groupId = parseInt(config.dedgeConfig.groupId);
        if (isNaN(groupId)) return null;
        const group = dedgeGroups.find((group) => group.id === groupId);
        return group ? { ...group, name: group.id.toString(), value: group.id, label: group.id.toString() } : null;
    };

    return isLoaded ? (
        <div className="view-wrapper">
            {engines.length > 0 && (
                <div className="c-page-header">
                    <div className="input-container w-25">
                        <div className="single-search-input">
                            <input type="text" placeholder="Rechercher une configuration moteur..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                    <div className="c-page-header__actions">
                        <button
                            className="button-large blue-button"
                            onClick={handleOpenModal}
                            disabled={!contact?.reference}
                            title={!contact?.reference ? "Le contact doit avoir une référence Altelis pour ajouter une configuration" : ""}
                        >
                            <PlusIcon size={14} />
                            <span>Ajouter une configuration</span>
                        </button>
                    </div>
                </div>
            )}

            {engines.length > 0 ? (
                filteredEngines.length > 0 ? (
                    <div className="engine-configs">
                        <div className="cards-container">
                            <div className="cards-box">
                                {filteredEngines.map((engine) => {
                                    const statusVariant = getStatusVariant(engine.engineType);

                                    return (
                                        <div key={engine.id} className="c-card">
                                            <div className="c-card__content">
                                                <div className="c-card__header">
                                                    <div className="c-card__title-section">
                                                        <h3 className="c-card__text">
                                                            <span className="c-card__title">{engine.hotelName || "Configuration moteur"}</span>
                                                        </h3>
                                                    </div>
                                                    <div className="c-card__info">
                                                        <div className="c-card__tag">
                                                            <CustomTag value={getEngineTypeLabel(engine.engineType)} className={`${statusVariant} uppercase`} />
                                                            <CustomTag value={engine.engineCode} className={`${statusVariant} uppercase`} />
                                                        </div>
                                                        <div className="c-card__actions">
                                                            <button className="c-card__action c-card__action--edit" onClick={() => setRateOverridesEngine(engine)} title="Exclusions de l'export métamoteurs">
                                                                <FunnelIcon size={14} />
                                                            </button>
                                                            <button className="c-card__action c-card__action--edit" onClick={() => handleEditEngine(engine)} title="Modifier la configuration">
                                                                <PencilIcon size={14} />
                                                            </button>
                                                            <button className="c-card__action c-card__action--delete" onClick={() => handleRemoveEngine(engine)} title="Supprimer la configuration">
                                                                <TrashIcon size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="c-card__engine-info">
                                                    <div className="c-card__engine-row clickable">
                                                        <div className="c-card__engine-icon c-card__engine-icon--url">
                                                            <GlobeIcon size={16} weight="bold" />
                                                        </div>
                                                        <div className="c-card__engine-content">
                                                            <div className="c-card__engine-label">URL Moteur</div>
                                                            <div className="c-card__engine-value">{engine.engineUrl}</div>
                                                        </div>
                                                        <a href={engine.engineUrl} target="_blank" rel="noopener noreferrer" className="c-card__detail-item-link">
                                                            <ArrowRightIcon size={16} weight="bold" />
                                                        </a>
                                                    </div>

                                                    {engine.stayTax && (
                                                        <div className="c-card__engine-row">
                                                            <div className="c-card__engine-icon c-card__engine-icon--tax">
                                                                <CurrencyEurIcon size={16} weight="bold" />
                                                            </div>
                                                            <div className="c-card__engine-content">
                                                                <div className="c-card__engine-label">Taxe de séjour</div>
                                                                <div className="c-card__engine-value">{engine.stayTax}€</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {engine.engineType === "dedge" && engine.dedgeConfig && (
                                                        <>
                                                            {engine.dedgeConfig.bookingUrl && (
                                                                <div className="c-card__engine-row clickable">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--booking">
                                                                        <BookOpenIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">URL Booking</div>
                                                                        <div className="c-card__engine-value">{engine.dedgeConfig.bookingUrl}</div>
                                                                    </div>
                                                                    <a href={engine.dedgeConfig.bookingUrl} target="_blank" rel="noopener noreferrer" className="c-card__detail-item-link">
                                                                        <ArrowRightIcon size={16} weight="bold" />
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {engine.dedgeConfig.expediaUrl && (
                                                                <div className="c-card__engine-row clickable">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--expedia">
                                                                        <MagnetIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">URL Expedia</div>
                                                                        <div className="c-card__engine-value">{engine.dedgeConfig.expediaUrl}</div>
                                                                    </div>
                                                                    <a href={engine.dedgeConfig.expediaUrl} target="_blank" rel="noopener noreferrer" className="c-card__detail-item-link">
                                                                        <ArrowRightIcon size={16} weight="bold" />
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    {engine.engineType === "reservit" && engine.reservitConfig && (
                                                        <>
                                                            {engine.reservitConfig.enginePrefix && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                        <TagIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">Préfixe moteur</div>
                                                                        <div className="c-card__engine-value">{engine.reservitConfig.enginePrefix}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {engine.reservitConfig.rateId && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--rate">
                                                                        <CoinIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">ID Prix</div>
                                                                        <div className="c-card__engine-value">{engine.reservitConfig.rateId}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    {engine.engineType === "mews" && engine.mewsConfig && (
                                                        <>
                                                            {engine.mewsConfig.mewsEnterpriseId && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                        <TagIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">Enterprise ID</div>
                                                                        <div className="c-card__engine-value">{engine.mewsConfig.mewsEnterpriseId}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {engine.mewsConfig.mewsServiceId && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                        <TagIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">Service ID</div>
                                                                        <div className="c-card__engine-value">{engine.mewsConfig.mewsServiceId}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {engine.mewsConfig.mewsConfigurationId && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                        <TagIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">Configuration ID</div>
                                                                        <div className="c-card__engine-value">{engine.mewsConfig.mewsConfigurationId}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {engine.mewsConfig.startOfDay && (
                                                                <div className="c-card__engine-row">
                                                                    <div className="c-card__engine-icon c-card__engine-icon--rate">
                                                                        <CoinIcon size={16} weight="bold" />
                                                                    </div>
                                                                    <div className="c-card__engine-content">
                                                                        <div className="c-card__engine-label">Début de journée</div>
                                                                        <div className="c-card__engine-value">{engine.mewsConfig.startOfDay}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    {engine.engineType === "mister_booking" && engine.misterBookingConfig && engine.misterBookingConfig.apiLogin && (
                                                        <div className="c-card__engine-row">
                                                            <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                <TagIcon size={16} weight="bold" />
                                                            </div>
                                                            <div className="c-card__engine-content">
                                                                <div className="c-card__engine-label">API Login</div>
                                                                <div className="c-card__engine-value">{engine.misterBookingConfig.apiLogin}</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {engine.engineType === "amenitiz" && engine.amenitizConfig && engine.amenitizConfig.hotelUuid && (
                                                        <div className="c-card__engine-row">
                                                            <div className="c-card__engine-icon c-card__engine-icon--prefix">
                                                                <TagIcon size={16} weight="bold" />
                                                            </div>
                                                            <div className="c-card__engine-content">
                                                                <div className="c-card__engine-label">UUID propriété Amenitiz</div>
                                                                <div className="c-card__engine-value">{engine.amenitizConfig.hotelUuid}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="no-data-container">
                        <p>Aucune configuration moteur ne correspond à votre recherche</p>
                    </div>
                )
            ) : (
                <div className="no-data-container">
                    {contact?.reference ? (
                        <>
                            <p>Aucune configuration moteur trouvée</p>
                            <button className="button-large blue-button" onClick={handleOpenModal}>
                                <PlusIcon size={14} />
                                <span>Créer une configuration</span>
                            </button>
                        </>
                    ) : (
                        <p>Le contact doit avoir une référence Altelis pour ajouter une configuration moteur.</p>
                    )}
                </div>
            )}

            <CustomModal show={isModalOpen} setShow={setIsModalOpen} title={isEditing ? "Modifier une configuration moteur" : "Nouvelle configuration moteur"}>
                <div className="custom__modal-group">
                    <label className="engine-type">
                        Type moteur <span className="required-asterisk">*</span>
                    </label>
                    <CustomSearchInput
                        key={isEditing ? `edit-${editingEngine?.id}` : "new"}
                        placeholder="Rechercher un moteur"
                        entity={selectableEngineTypes.map((engineType) => ({ ...engineType, id: engineType.value }))}
                        labelType="constant"
                        value={getSelectedEngineType()}
                        onChange={(newValue) => setConfig((config) => ({ ...config, engineType: newValue.value }))}
                        required
                    />
                    {isConfigDeletedOnSave && (
                        <p className="waiting-order-warning danger">
                            <span>
                                <strong>Attention : </strong>
                                changer de type supprimera définitivement la configuration {getEngineTypeLabel(editingEngine.engineType)} de ce moteur (identifiants et tokens compris).
                            </span>
                        </p>
                    )}
                </div>

                <div className="custom__modal-group">
                    <label className="hotel">
                        Nom de l'hôtel <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Entrez un nom"
                        value={config.hotelName || ""}
                        onChange={(e) => setConfig((config) => ({ ...config, hotelName: e.target.value }))}
                        className="classic-input"
                        required
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="code">
                        Code moteur <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Entrez un code moteur"
                        value={config.engineCode || ""}
                        onChange={(e) => setConfig((config) => ({ ...config, engineCode: e.target.value }))}
                        className="classic-input"
                        required
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="url">
                        URL moteur <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Entrez une URL"
                        value={config.engineUrl || ""}
                        onChange={(e) => setConfig((config) => ({ ...config, engineUrl: e.target.value }))}
                        className="classic-input"
                        required
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="tax">
                        Taxe de séjour <span className="required-asterisk">*</span>
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Entrez une taxe de séjour"
                        value={config.stayTax || ""}
                        onChange={(e) => setConfig((config) => ({ ...config, stayTax: e.target.value }))}
                        className="classic-input"
                        required
                    />
                </div>

                {config.engineType === "dedge" && (
                    <>
                        <div className="custom__modal-group">
                            <label className="booking">URL Booking</label>
                            <input
                                type="text"
                                placeholder="Entrez une URL Booking"
                                value={config.dedgeConfig?.bookingUrl || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, bookingUrl: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="url">URL Expedia</label>
                            <input
                                type="text"
                                placeholder="Entrez une URL Expedia"
                                value={config.dedgeConfig?.expediaUrl || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, expediaUrl: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="token">Access Token</label>
                            <input
                                type="text"
                                placeholder="Entrez un token d'accès"
                                value={config.dedgeConfig?.accessToken || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, accessToken: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="token">Secret Token</label>
                            <input
                                type="text"
                                placeholder="Entrez un token secret"
                                value={config.dedgeConfig?.secretToken || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, secretToken: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="tax">Taxe de séjour personnalisée</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Taxe partielle incluse dans le prix"
                                value={config.dedgeConfig?.customStayTax || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, customStayTax: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="id">ID Groupe D-EDGE</label>
                            <CustomSearchInput
                                key={`dedge-group-${config.dedgeConfig?.groupId || "new"}`}
                                placeholder="Sélectionner un groupe D-EDGE"
                                entity={dedgeGroups.map((group) => ({
                                    ...group,
                                    name: group.id.toString(),
                                    label: group.id.toString(),
                                    value: group.id,
                                }))}
                                labelType="name"
                                value={getSelectedDedgeGroup()}
                                onChange={(newValue) => setConfig((config) => ({ ...config, dedgeConfig: { ...config.dedgeConfig, groupId: newValue?.id || null } }))}
                                isClearable={true}
                                className="modal-select"
                            />
                        </div>
                    </>
                )}

                {config.engineType === "reservit" && (
                    <>
                        <div className="custom__modal-group">
                            <label className="prefix">Préfixe moteur</label>
                            <input
                                type="text"
                                placeholder="Entrez un préfixe"
                                value={config.reservitConfig?.enginePrefix || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, reservitConfig: { ...config.reservitConfig, enginePrefix: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="id">Identifiant prix</label>
                            <input
                                type="text"
                                placeholder="Entrez un identifiant"
                                value={config.reservitConfig?.rateId || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, reservitConfig: { ...config.reservitConfig, rateId: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>
                    </>
                )}

                {config.engineType === "mews" && (
                    <>
                        <div className="custom__modal-group">
                            <label className="token">Client Token</label>
                            <input
                                type="text"
                                placeholder="Entrez un client token"
                                value={config.mewsConfig?.clientToken || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, clientToken: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="token">Access Token</label>
                            <input
                                type="text"
                                placeholder="Entrez un access token"
                                value={config.mewsConfig?.accessToken || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, accessToken: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="id">Service ID</label>
                            <input
                                type="text"
                                placeholder="Entrez un service ID"
                                value={config.mewsConfig?.mewsServiceId || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, mewsServiceId: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="id">Enterprise ID</label>
                            <input
                                type="text"
                                placeholder="Entrez un enterprise ID"
                                value={config.mewsConfig?.mewsEnterpriseId || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, mewsEnterpriseId: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="id">Configuration ID</label>
                            <input
                                type="text"
                                placeholder="Entrez un configuration ID"
                                value={config.mewsConfig?.mewsConfigurationId || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, mewsConfigurationId: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="time">Début de journée (HH:MM:SS)</label>
                            <input
                                type="text"
                                placeholder="00:00:00"
                                pattern="^\d{2}:\d{2}:\d{2}$"
                                value={config.mewsConfig?.startOfDay || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, mewsConfig: { ...config.mewsConfig, startOfDay: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>
                    </>
                )}

                {config.engineType === "mister_booking" && (
                    <>
                        <div className="custom__modal-group">
                            <label className="login">API Login</label>
                            <input
                                type="text"
                                placeholder="Entrez un login API"
                                value={config.misterBookingConfig?.apiLogin || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, misterBookingConfig: { ...config.misterBookingConfig, apiLogin: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="password">API Password</label>
                            <input
                                type="password"
                                placeholder="Entrez un mot de passe API"
                                value={config.misterBookingConfig?.apiPassword || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, misterBookingConfig: { ...config.misterBookingConfig, apiPassword: e.target.value } }))}
                                className="classic-input"
                            />
                        </div>
                    </>
                )}

                {config.engineType === "amenitiz" && (
                    <>
                        <div className="custom__modal-group">
                            <label className="id">UUID propriété (hotel_id)</label>
                            <input
                                type="text"
                                placeholder="ex. cbcc1712-6b3f-4f53-8e23-f03d999c0c57"
                                value={config.amenitizConfig?.hotelUuid || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, amenitizConfig: { ...config.amenitizConfig, hotelUuid: e.target.value.trim() } }))}
                                className="classic-input"
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="token">Jeton API (Bearer)</label>
                            <input
                                type="password"
                                placeholder="Jeton fourni par le support Amenitiz pour cette propriété"
                                value={config.amenitizConfig?.accessToken || ""}
                                onChange={(e) => setConfig((config) => ({ ...config, amenitizConfig: { ...config.amenitizConfig, accessToken: e.target.value.trim() } }))}
                                className="classic-input"
                            />
                        </div>
                    </>
                )}

                <div className="modal-buttons">
                    <button onClick={() => setIsModalOpen(false)} className="button-large cancel-button" disabled={isCreating}>
                        Annuler
                    </button>
                    <button onClick={handleCreateEngine} className="button-large green-button validate" disabled={isCreating}>
                        {isCreating ? <CircularProgress size={16} className="modal-progress" /> : isEditing ? <PencilIcon size={16} /> : <PlusIcon size={16} />}
                        <span>{isCreating ? (isEditing ? "Modification..." : "Création...") : isEditing ? "Modifier la configuration" : "Créer la configuration"}</span>
                    </button>
                </div>
            </CustomModal>

            <CustomModal
                show={rateOverridesEngine !== null}
                setShow={(show) => !show && setRateOverridesEngine(null)}
                title={`Export métamoteurs : ${rateOverridesEngine?.hotelName || rateOverridesEngine?.engineCode || ""}`}
                size="wide"
            >
                {rateOverridesEngine && <RateOverrides key={rateOverridesEngine.engineCode} engineCode={rateOverridesEngine.engineCode} />}
            </CustomModal>

            <CustomModal show={deleteModalOpen} setShow={setDeleteModalOpen} title="Confirmer la suppression">
                <div className="c-modal-content is-center">
                    <p>
                        Êtes-vous sûr de vouloir supprimer la configuration moteur <strong>"{deleteTarget?.hotelName || "sans nom"}"</strong> ?
                    </p>
                    <p>Cette action est irréversible.</p>
                </div>
                <div className="modal-buttons">
                    <button onClick={() => setDeleteModalOpen(false)} className="button-large cancel-button">
                        Annuler
                    </button>
                    <button onClick={handleDeleteEngine} className="button-large red-button">
                        <TrashIcon size={16} weight="bold" />
                        <span>Supprimer</span>
                    </button>
                </div>
            </CustomModal>
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default Engines;
